import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  projectTreeItemTypeOptions,
  projectTreeSortByOptions,
  projectTreeSortDirectionOptions,
  projectTreeStateFilterOptions,
  useGetProjectTreeQuery
} from '../../../../react-query/project/query/use-get-project-tree-query'
import {
  LoadingNextPage,
  ProjectMenuTileListContainer,
  TileToolbarContainer
} from '../../styled-component'
import {
  projectMenuItemTypeOptions,
  projectMenuTabOptions
} from '../../constants'
import { debounce, isEmpty } from 'lodash'
import { useOnInView } from 'react-intersection-observer'
import { useHistory } from 'react-router-dom'
import TileCardComponent from '../../../../components/tile-card'
import ProjectMenuTileLoadList from './components/project-menu-tile-load-list'
import ProjectMenuTileToolbar from './components/project-menu-tile-toolbar'
import ProjectMenuTileBackButton from './components/project-menu-tile-back-button'
import ProjectMenuTileEmptySearch from './components/project-menu-tile-empty-search'
import ProjectMenuTileEmptyProjectTree from './components/project-menu-tile-empty-project-tree'
import { useMeQuery } from '../../../../react-query/authentication/query/use-me-query'
import { SpinnerGrey } from '../../../../components/stories/atoms/icons/special'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import { useProjectMenuStore } from '../../../../zustand/project-menu/use-project-menu-store'
import { useProjectMenuListViewControl } from '../../../../hooks/use-project-menu-list-view-control'
import { serializeQueryString } from '../../../../utils/urlManager'
import CompanyProjectsContextMenu from '../company-projects-context-menu'

const emptyUserPermissions = []
const ProjectMenuTileCompanyProjects = () => {
  const history = useHistory()
  const { viewType, tab } = useProjectMenuListViewControl()

  const tileViewFolderHistory = useProjectMenuStore(
    (state) => state.tileViewFolderHistory
  )
  const setTileViewFolderHistory = useProjectMenuStore(
    (state) => state.setTileViewFolderHistory
  )

  const search = useProjectMenuStore((state) => state.projectMenuSearch)
  const setSearch = useProjectMenuStore((state) => state.setProjectMenuSearch)
  const showInactive = useProjectMenuStore(
    (state) => state.companyProjectsShowInactive
  )
  const setShowInactive = useProjectMenuStore(
    (state) => state.setCompanyProjectsShowInactive
  )

  const setPageTitle = useProjectMenuStore((state) => state.setPageTitle)

  // derived states
  const currentFolderId = tileViewFolderHistory.at(-1)?.id ?? null

  const stateFilter = showInactive
    ? projectTreeStateFilterOptions.ALL
    : projectTreeStateFilterOptions.ACTIVE

  const hasFolderHistory = tileViewFolderHistory.length > 0

  const queryParams = useMemo(
    () => ({
      stateFilter,
      search,
      sortDirection: projectTreeSortDirectionOptions.DESC,
      sortBy: projectTreeSortByOptions.LAST_ACTIVITY
    }),
    [stateFilter, search]
  )

  // queries
  const {
    data: projectTree,
    isLoading: projectTreeIsLoading,
    isFetchingNextPage: nextPageIsLoading,
    fetchNextPage,
    hasNextPage
  } = useGetProjectTreeQuery({
    folderId: currentFolderId,
    params: queryParams
  })

  const projectList = useMemo(
    () => projectTree?.pages.flatMap((p) => p.content) ?? [],
    [projectTree]
  )

  const { data: user } = useMeQuery()

  const userPermissions = useMemo(
    () => user?.permissions ?? emptyUserPermissions,
    [user?.permissions]
  )

  const hasProjects = projectList.length > 0
  const isSearching = !isEmpty(search)

  const isEmptySearch = isSearching && !hasProjects
  const isEmptyState = !isSearching && !hasProjects

  // handlers
  const [inputValue, setInputValue] = useState(search)

  useEffect(() => {
    setInputValue(search)
  }, [search])

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 400), [])

  const handleSearch = useCallback(
    (value) => {
      setInputValue(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch]
  )

  const handleCreateProject = () => {
    history.push(`/project/new?view=${viewType}&tab=${tab}`)
  }

  const goBackFolder = () => {
    setTileViewFolderHistory(tileViewFolderHistory.slice(0, -1))
  }

  const openItem = (item) => {
    if (item.type === projectMenuItemTypeOptions.FOLDER) {
      setTileViewFolderHistory([
        ...tileViewFolderHistory,
        { id: item.id, name: item.name }
      ])
      return
    }

    history.push(`/project/${item.id}`)
  }

  // infinite scroll
  const onBottomReached = useCallback(() => {
    if (!hasNextPage || nextPageIsLoading) return
    fetchNextPage()
  }, [hasNextPage, nextPageIsLoading])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomRef = useOnInView(
    useCallback((inView) => {
      if (inView) onBottomReachedRef.current?.()
    }, []),
    { rootMargin: '20px', skip: !hasNextPage }
  )

  // effects
  useEffect(() => {
    const currentFolderName = tileViewFolderHistory.at(-1)?.name ?? null
    setPageTitle(currentFolderName)
  }, [tileViewFolderHistory])

  // renders
  const ProjectTree = useCallback(
    () =>
      projectList.map((item) => {
        const isFolder = item.type === projectMenuItemTypeOptions.FOLDER

        const handleMoveItem = () => {
          if (item.type === projectTreeItemTypeOptions.FOLDER) {
            history.push(
              `/project/move/folder/${item.id}?view=${viewType}&tab=${tab}`
            )
            return
          }

          history.push(
            `/project/move/project/${item.id}?view=${viewType}&tab=${tab}`
          )
        }

        const handleCopyProject = () => {
          if (item.type !== projectTreeItemTypeOptions.PROJECT) return

          const queryString = serializeQueryString({
            ...(item.parentId && { folderId: item.parentId }),
            view: viewType,
            tab
          })

          history.push(`/project/${item.id}/copy${queryString}`)
        }

        return (
          <TileCardComponent
            key={item.id}
            item={item}
            isFolder={isFolder}
            onClick={() => openItem(item)}
            metaData={!isFolder && { lastActivity: item.lastActivity }}
            disabled={!item.active}
            contextMenu={
              <CompanyProjectsContextMenu
                item={item}
                userPermissions={userPermissions}
                onCopyProject={handleCopyProject}
                onMoveItem={handleMoveItem}
                isTileView
              />
            }
            onOpenSettings={(settingsItem) =>
              history.push(
                `/project/settings/${settingsItem.id}?view=${viewType}&tab=${tab}`
              )
            }
          />
        )
      }),
    [projectList, userPermissions, viewType, tab, history]
  )

  const ProjectTileList = () => {
    if (isEmptySearch) return <ProjectMenuTileEmptySearch />

    return (
      <>
        <ProjectMenuTileBackButton
          hasFolderHistory={hasFolderHistory}
          goBackFolder={goBackFolder}
        />
        {isEmptyState ? (
          <ProjectMenuTileEmptyProjectTree
            handleBackFolder={goBackFolder}
            handleCreateProject={handleCreateProject}
            projectListOption={projectMenuTabOptions.COMPANY_PROJECTS}
            isFolder={hasFolderHistory}
            userPermissions={userPermissions}
          />
        ) : (
          <ProjectTree />
        )}
      </>
    )
  }

  return (
    <>
      <TileToolbarContainer>
        <ProjectMenuTileToolbar
          setSearch={handleSearch}
          search={inputValue}
          showInactive={showInactive}
          setShowInactive={setShowInactive}
        />
      </TileToolbarContainer>

      <ProjectMenuTileListContainer>
        <ProjectMenuTileLoadList isLoading={projectTreeIsLoading}>
          <ProjectTileList />
        </ProjectMenuTileLoadList>
      </ProjectMenuTileListContainer>
      {nextPageIsLoading && (
        <LoadingNextPage>
          <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
        </LoadingNextPage>
      )}
      <div ref={bottomRef} style={{ height: 1 }} />
    </>
  )
}

export default ProjectMenuTileCompanyProjects
